"use client";

import React from "react";
import { SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import parse from "html-react-parser";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { SectionWrapper } from "../components/SectionWraper";
import { useState } from "react";
import { CarouselApi, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
  
interface GalleryItem {
  image?: string;
  imageAltText?: string;
  hasIconBadge?: boolean;
  icon1?: string;
  icon2?: string;
  text?: string;
  title?: string;
  content?: string;
}

interface Gallery7Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  content?: string; 
  galleryItems?: GalleryItem[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Gallery7 = ({
  darkMode = false,
  variant = "A",
  spacing = "xlarge",
  usedInPageNav = false,
  pageNavTitle = "Gallery 7",
  themeChangeOnScroll = false,
  title,
  content,
  galleryItems,
  attributes,
}: Gallery7Props) => {

  const [api, setApi] = useState<CarouselApi>();
  const [progress, setProgress] = useState(Math.floor(100 / (galleryItems?.length ?? 1)));


  useEffect(() => {
    if (!api) {
      return;
    }

    setProgress(Math.max(1 / (galleryItems?.length ?? 1), Math.min(1, api.scrollProgress())) * 100);

    api.on("select", () => {
      setProgress(Math.max(1 / (galleryItems?.length ?? 1), Math.min(1, api.scrollProgress())) * 100);
    });
  }, [api, galleryItems?.length]);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="gallery7"
      attributes={attributes}
      className="overflow-hidden"
    >   
      <div className="container" data-aos="fade-up">
        <div className="flex w-full flex-col gap-4 text-center md:text-left">
          {title && (
            <h2 className="md:text-center">
              {parse(title)}
            </h2>
          )}
          {content && (
            <div className="paragraph-lg text-muted-foreground md:max-w-[65ch] md:text-center md:mx-auto">
              {parse(content)}
            </div>
          )}
        </div>
        <Carousel
          opts={{
            align: "start",
          }}
          className="w-full"
          setApi={setApi}
        >
          <CarouselContent className="-ml-8 mt-16 md:mt-20">
            {galleryItems?.map((item, index) => (
              <CarouselItem
                key={index}
                className="pl-8 md:basis-1/2 lg:basis-1/3"
              >
                <Card className="border-0 shadow-none p-0">
                  <CardContent className="flex flex-col p-0 gap-6">
                    <div
                      style={{ backgroundImage: `url("${item?.image}")` }}
                      className="relative flex aspect-[418/451] w-full flex-col items-center justify-between overflow-hidden rounded-2xl bg-cover bg-center bg-no-repeat p-8"
                    >
                      <div className="flex size-full flex-1"></div>
                      {item.hasIconBadge && (
                        <div className="h-12 w-full">
                          <div className="bg-background/75 backdrop-blur supports-[backdrop-filter]:bg-background/50 mx-auto mb-8 flex w-full max-w-[15rem] items-center gap-2 rounded-full px-6 py-2.5 shadow-xl">
                            <div className="shrink-0">
                              <div className="flex -space-x-2">
                                {item.icon1 && (
                                <div className="bg-primary flex size-7 rounded-full text-primary-foreground items-center justify-center">
                                  <span className="material-symbols-outlined !size-4 !text-[16px]">
                                    {item.icon1}
                                  </span>
                                </div>
                                )}
                                {item.icon2 && (
                                <div className="bg-primary flex size-7 rounded-full text-primary-foreground items-center justify-center">
                                  <span className="material-symbols-outlined !size-4 !text-[16px]">
                                    {item.icon2}
                                  </span>
                                </div>
                                )}
                              </div>
                            </div>
                            <div className="paragraph-sm text-center w-full">
                              {item.text}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex w-full flex-col gap-1">
                      {item.title && (
                        <h5>
                          {item.title}
                        </h5>
                      )}
                      {item.content && (
                        <div className="paragraph-base mt-2">{parse(item.content)}</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="mt-20 hidden items-center justify-center gap-4 md:flex">
            <CarouselPrevious className="static size-12 translate-y-0" />
            <CarouselNext className="static size-12 translate-y-0" />
          </div>
          <div className="mt-8 flex w-full items-center gap-2 md:hidden">
            <Progress value={progress} className="h-[2px] w-full" />
          </div>
        </Carousel>
      </div>
    </SectionWrapper>
  );
};

export { Gallery7 };
